import { apiFetch, apiJson } from './api';

// ─────────────────────────────────────────────────────────────────────
// Embedded (white-label) checkout — Stripe Elements
//
// The redirect-based startCheckout / startHintPackCheckout helpers
// below remain available as a fallback, but the live UI uses these
// `*Intent` helpers to render the payment form inside hexponents.com
// instead of bouncing to checkout.stripe.com.
// ─────────────────────────────────────────────────────────────────────

export interface StripeConfig { publishableKey: string }

/// Cached publishable key — small, safe to keep in module scope.
let configCache: StripeConfig | null = null;
export async function fetchStripeConfig(): Promise<StripeConfig | null> {
  if (configCache) return configCache;
  const data = await apiJson<StripeConfig>('/billing/stripe/config');
  if (data?.publishableKey) configCache = data;
  return configCache;
}

export interface IntentResult {
  clientSecret: string;
  // Subscription returns a subscriptionId; hint pack returns paymentIntentId.
  subscriptionId?: string;
  paymentIntentId?: string;
}

export async function createSubscriptionIntent(
  cadence: 'monthly' | 'yearly',
): Promise<{ ok: true; intent: IntentResult } | { ok: false; error: string }> {
  const res = await apiFetch('/billing/stripe/create-subscription-intent', {
    method: 'POST',
    body: JSON.stringify({ cadence }),
  });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.clientSecret !== 'string') return { ok: false, error: 'no_client_secret' };
  return { ok: true, intent: data as IntentResult };
}

export async function createHintPackIntent(): Promise<
  { ok: true; intent: IntentResult } | { ok: false; error: string }
> {
  const res = await apiFetch('/billing/stripe/create-hint-pack-intent', { method: 'POST' });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.clientSecret !== 'string') return { ok: false, error: 'no_client_secret' };
  return { ok: true, intent: data as IntentResult };
}

// ─────────────────────────────────────────────────────────────────────
// Account view — subscription read, change card, cancel, resume.
// All white-label except the existing /create-portal-session
// fallback (kept so power users can still hit Stripe's full portal).
// ─────────────────────────────────────────────────────────────────────

export interface SubscriptionState {
  plan: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  cardBrand: string | null;
  cardLast4: string | null;
}

export async function fetchSubscription(): Promise<SubscriptionState | null> {
  const data = await apiJson<{ subscription: SubscriptionState | null }>('/billing/stripe/me/subscription');
  return data?.subscription ?? null;
}

/// One row in the in-app billing history. Mirrors the shape the server
/// returns from `GET /billing/stripe/me/invoices` — minimum the UI
/// needs to render the row + open the Stripe-hosted invoice/PDF.
export interface InvoiceRow {
  id: string;
  number: string | null;
  createdAt: string;
  amountPaid: number; // cents
  currency: string;
  status: string;
  hostedUrl: string | null;
  pdfUrl: string | null;
}

export async function fetchInvoices(): Promise<InvoiceRow[]> {
  const data = await apiJson<{ invoices: InvoiceRow[] }>('/billing/stripe/me/invoices');
  return data?.invoices ?? [];
}

export async function createSetupIntent(): Promise<
  { ok: true; intent: IntentResult } | { ok: false; error: string }
> {
  const res = await apiFetch('/billing/stripe/create-setup-intent', { method: 'POST' });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.clientSecret !== 'string') return { ok: false, error: 'no_client_secret' };
  return { ok: true, intent: data as IntentResult };
}

export async function cancelSubscription(): Promise<boolean> {
  const res = await apiFetch('/billing/stripe/cancel-subscription', { method: 'POST' });
  return res.ok;
}

export async function resumeSubscription(): Promise<boolean> {
  const res = await apiFetch('/billing/stripe/resume-subscription', { method: 'POST' });
  return res.ok;
}

// Thin client for the Stripe billing routes on the server. Each call
// asks the server to create a Stripe-hosted page (Checkout / Customer
// Portal) and returns a URL the browser should navigate to.

export async function startCheckout(
  cadence: 'monthly' | 'yearly' = 'monthly',
): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  const res = await apiFetch('/billing/stripe/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ cadence }),
  });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.url !== 'string') return { ok: false, error: 'no_checkout_url' };
  return { ok: true, url: data.url };
}

export async function openCustomerPortal(): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  const res = await apiFetch('/billing/stripe/create-portal-session', { method: 'POST' });
  if (!res.ok) {
    let error = 'request_failed';
    try { error = (await res.json()).error ?? error; } catch {/* ignore */}
    return { ok: false, error };
  }
  const data = await res.json();
  if (typeof data?.url !== 'string') return { ok: false, error: 'no_portal_url' };
  return { ok: true, url: data.url };
}
